import type { Immutable } from "immer";
import React from "react";
import { getGlobalSpacingClasses } from "tsi-common-react/src/components/_spacing/globalSpacing";

import type { OffersBlock as OffersBlockValue } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

type Value = OffersBlockValue;
type Offer = NonNullable<Value["offers"]>[number];

/**
 * Faithful port of the legacy server-rendered ``cms/blocks/offers-block.html``.
 *
 * All richtext fields (``header_content`` and each offer's ``lead_in``,
 * ``title``, ``subtitle``, ``content``) were rendered via ``{% include_block %}``,
 * which emits ``<div class="rich-text">``-wrapped HTML. The live SCSS targets
 * these with a CHILD combinator (``.offers-block > .rich-text``) and a
 * ``:nth-of-type(3)`` rule (``.offer .rich-text:nth-of-type(3)``), both of which
 * require each ``.rich-text`` to be a DIRECT child of its container -- so the
 * block serializes each field as BARE inner HTML (``offers.py``
 * ``get_data_for_client_render`` / ``expand_db_html``) and the wrapper is added
 * here via ``<RichTextBlock className="rich-text">`` (exactly one ``.rich-text``
 * div, not the double wrapper a pre-wrapped value + ``RichTextBlock`` would
 * produce). Empty optional fields serialize to ``""`` and render
 * ``<div class="rich-text"></div>`` (matching the legacy empty-field render).
 *
 * The support icon uses the ``width-50`` rendition (legacy
 * ``{% wagtail_image offer.support_icon specs='width-50' class='icon' %}``) and
 * is omitted entirely when no icon is set (legacy ``{% wagtail_image %}`` of a
 * ``None`` image renders nothing).
 */
const OfferElem = (props: Immutable<{ offer: Offer }>): React.JSX.Element => {
    const offer = props.offer;
    const icon = offer.support_icon;
    return (
        <div className="offer">
            {icon ? (
                <img
                    className="icon"
                    src={icon.renditions["width-50"]?.url ?? icon.url}
                    alt={icon.title}
                    loading="lazy"
                    decoding="async"
                />
            ) : null}

            <RichTextBlock value={offer.lead_in ?? ""} className="rich-text" />

            <div className="title">
                <RichTextBlock
                    value={offer.title ?? ""}
                    className="rich-text"
                />
            </div>

            <RichTextBlock value={offer.subtitle ?? ""} className="rich-text" />

            <RichTextBlock value={offer.content ?? ""} className="rich-text" />
        </div>
    );
};

export const OffersBlock = (
    props: Immutable<{ value: OffersBlockValue }>,
): React.JSX.Element => {
    const v = props.value;
    return (
        <section
            className={concatClassNames([
                "offers-block",
                getGlobalSpacingClasses(v.spacing),
            ])}
        >
            <RichTextBlock
                value={v.header_content ?? ""}
                className="rich-text"
            />

            {(v.offers ?? []).map((offer, i) => (
                <OfferElem key={i} offer={offer} />
            ))}
        </section>
    );
};
